import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BookmarkPlus, Copy, Trash2, Loader2, Check } from "lucide-react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { SECTION_TEXT, copyEverything, copyText } from "@/lib/content";
import CardSection from "@/components/content/CardSection";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Prompt = ({ children, testid }) => (
  <p className="text-[13px] leading-relaxed text-[#1A1325]/85 bg-[#FAF7FE] border border-[#F0EAF7] rounded-[16px] p-4 whitespace-pre-wrap" data-testid={testid}>{children}</p>
);

const Meta = ({ k, v }) => <p className="text-xs"><span className="text-[#9C4DDC] uppercase tracking-[0.12em] text-[10px] mr-2">{k}</span><span className="muted">{v}</span></p>;

function Building() {
  return (
    <div className="panel p-10 text-center fade-up" data-testid="card-generating">
      <Loader2 className="animate-spin text-[#9C4DDC] mx-auto mb-5" size={32} strokeWidth={1.5} />
      <p className="font-display text-2xl">Building her post…</p>
      <p className="muted text-sm mt-2">Locking identity, writing shots, caption and DM. About 30 seconds.</p>
    </div>
  );
}

export function Sections({ o, regen }) {
  const s = (id, title, body) => <CardSection key={id} id={id} title={title} text={SECTION_TEXT[id](o)} onRegenerate={regen}>{body}</CardSection>;
  return (
    <div className="space-y-5">
      {o.video_shots.length > 0 && s("video_shots", "Video shots", (
        <div className="space-y-5">{o.video_shots.map((v, i) => (
          <div key={i} className="space-y-2" data-testid={`video-shot-${i}`}>
            <div className="flex items-center gap-3"><span className="font-display text-lg text-[#9C4DDC]">{v.label}</span><span className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border border-[#F0EAF7] muted">{v.duration_seconds}s</span></div>
            <Meta k="Camera" v={v.camera} /><Meta k="Action" v={v.action} />{v.dialogue && <Meta k="Dialogue" v={v.dialogue} />}
            <Prompt testid={`video-shot-prompt-${i}`}>{v.prompt}</Prompt>
          </div>))}</div>
      ))}
      {o.image_prompts.length > 0 && s("image_prompts", "Image prompts", (
        <div className="space-y-4">{o.image_prompts.map((p, i) => (
          <div key={i}><p className="text-[10px] uppercase tracking-[0.15em] text-[#9C4DDC] mb-2">{p.label}</p><Prompt testid={`image-prompt-${i}`}>{p.prompt}</Prompt></div>))}</div>
      ))}
      {o.carousel_slides.length > 0 && s("carousel_slides", `Carousel slides (${o.carousel_slides.length})`, (
        <ol className="space-y-3" data-testid="carousel-slides-list">{o.carousel_slides.map((c) => (
          <li key={c.slide} className="flex gap-4" data-testid={`carousel-slide-${c.slide}`}>
            <span className="font-display text-2xl text-[#9C4DDC] w-7 shrink-0">{c.slide}</span>
            <div><p className="text-sm">{c.text}</p>{c.visual_note && <p className="text-xs muted mt-1">{c.visual_note}</p>}</div>
          </li>))}</ol>
      ))}
      {o.on_screen_text && s("on_screen_text", "On-screen text", <p className="font-display text-2xl leading-snug whitespace-pre-line" data-testid="card-on-screen-text">{o.on_screen_text}</p>)}
      {s("caption", "Caption", <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="card-caption">{o.caption}</p>)}
      {s("hashtags", "Hashtags", <p className="text-sm text-[#9C4DDC]" data-testid="card-hashtags">{o.hashtags.join(" ")}</p>)}
      {s("keyword_dm", "Comment keyword + DM", (
        <div className="space-y-4">
          <span className="inline-block font-bold tracking-[0.2em] text-[#9C4DDC] bg-[#F1E9FE] px-5 py-2 rounded-full" data-testid="card-comment-keyword">{o.comment_keyword}</span>
          <p className="text-sm whitespace-pre-wrap bg-[#FAF7FE] border border-[#F0EAF7] rounded-[16px] p-4" data-testid="card-dm-message">{o.dm_message}</p>
        </div>
      ))}
      {s("posting_tips", "Posting tips", <ul className="space-y-2" data-testid="card-posting-tips">{o.posting_tips.map((t, i) => <li key={i} className="text-sm flex gap-3"><Check size={14} className="text-[#9C4DDC] mt-1 shrink-0" />{t}</li>)}</ul>)}
    </div>
  );
}

export default function CardView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => api.get(`/cards/${id}`).then(({ data }) => setCard(data)).catch(() => nav("/library")), [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (card?.gen_state !== "pending") return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [card?.gen_state, load]);

  if (!card) return <p className="muted text-sm">Loading…</p>;

  const regen = async (section) => {
    try {
      const { data } = await api.post(`/cards/${id}/regenerate`, { section });
      setCard((c) => ({ ...c, output: data.output }));
      toast.success("Section regenerated");
    } catch (err) { toast.error(formatErr(err)); }
  };
  const setStatus = async (status) => {
    try {
      await api.patch(`/cards/${id}`, { status });
      setCard((c) => ({ ...c, status }));
      toast.success(status === "generated" ? "Saved to Library" : `Marked as ${status}`);
    } catch (err) { toast.error(formatErr(err)); }
  };
  const remove = async () => {
    await api.delete(`/cards/${id}`);
    toast.success("Card deleted");
    nav("/library");
  };
  const done = card.gen_state === "done";

  return (
    <div className="max-w-3xl pb-10">
      <Link to="/library" className="text-xs uppercase tracking-[0.15em] muted flex items-center gap-2 mb-8" data-testid="card-back-link"><ArrowLeft size={14} /> Library</Link>
      <header className="flex items-start gap-4 mb-8 fade-up">
        {card.muse?.face_reference_url && <img src={imgUrl(card.muse.face_reference_url)} alt="" className="w-14 h-14 rounded-full object-cover border border-[#9C4DDC]/50" />}
        <div className="min-w-0">
          <p className="eyebrow">{card.muse?.name} · {card.format_key.replace(/_/g, " ")}</p>
          <h1 className="quote text-xl sm:text-2xl mt-2" data-testid="card-quote">“{card.quote}”</h1>
        </div>
      </header>

      {card.gen_state === "pending" && <Building />}
      {card.gen_state === "error" && (
        <div className="panel p-8 text-center" data-testid="card-error"><p className="font-display text-xl">{card.error}</p>
          <Link to="/create" className="btn-secondary mt-6">Try again</Link></div>
      )}
      {done && (
        <>
          <div className="flex flex-wrap gap-3 mb-8 items-center">
            {card.status === "draft" ? (
              <button className="btn-primary" onClick={() => setStatus("generated")} data-testid="card-save-library-button"><BookmarkPlus size={14} /> Save to Library</button>
            ) : (
              <select className="field w-auto py-2.5 text-xs uppercase tracking-[0.1em]" value={card.status} onChange={(e) => setStatus(e.target.value)} data-testid="card-status-select">
                <option value="generated">Generated</option><option value="posted">Posted</option>
              </select>
            )}
            <button className="btn-secondary" onClick={async () => { await copyText(copyEverything(card)); toast.success("Copied ✓"); }} data-testid="card-copy-everything-button"><Copy size={14} /> Copy everything</button>
            <button className="ml-auto muted hover:text-red-400 p-2" onClick={() => setConfirm(true)} data-testid="card-delete-button" aria-label="Delete"><Trash2 size={18} /></button>
          </div>
          <Sections o={card.output} regen={regen} />
        </>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#F0EAF7] rounded-[16px] max-w-[92vw] sm:max-w-md" data-testid="card-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-normal text-2xl">Delete this card?</AlertDialogTitle>
            <AlertDialogDescription className="muted">This can't be undone. Your post count won't be refunded.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#F0EAF7] rounded-[16px]" data-testid="card-delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-600 text-white rounded-[16px]" onClick={remove} data-testid="card-delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
